import { getCrmFeatureFlags } from 'common/api/crm/feature-flags';
import { FeaturesState } from 'common/features/featuresReducer';
import { createSlice } from 'common/modules/create-slice';
import { UserAuthState } from '../account/duck';
import { AnyAction, Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { IS_FLIPGIVE } from 'common/env';

export enum FeatureFlagTypes {
    allow_buynow_mobile = 'buy_now',
    allow_bulk_gifting = 'allow_bulk_gifting',
    coordinator_toggles = 'coordinator_toggles',
    enable_invite_friends_family = 'enable_invite_friends_family',
    enable_unified_coordinator_dashboard = 'enable_unified_coordinator_dashboard',
    enable_bulk_egifting = 'enable_bulk_egifting',
    enable_coordinator_vacation_mode = 'enable_coordinator_vacation_mode',
    enable_coordinator_checklist = 'enable_coordinator_checklist',
    enable_iterable = 'enable_iterable',
    enable_constructor_search_web = 'enable_constructor_search_web',
    enable_constructor_browse_web = 'enable_constructor_browse_web',
    enable_constructor_browse_app = 'enable_constructor_browse_app',
    enable_debit_app = 'enable_debit_app',
    enable_debit_web = 'enable_debit_web',
    enable_payment_banner_app = 'enable_payment_banner_app',
    enable_buy_now_payment_app = 'enable_buy_now_payment_app',
    enable_remote_activation_app = 'enable_remote_activation_app',
    enable_rokt_app = 'enable_rokt_app',
    enable_add_money_now_payment_app = 'enable_add_money_now_payment_app',
    enable_rokt_web = 'enable_rokt_web',
    enable_buy_now_web = 'enable_buy_now_web',
    enable_amex_egift_app = 'enable_amex_egift_app',
    enable_amex_egift_web = 'enable_amex_egift_web',
    enable_constructor_browse_with_affiliate_app = 'enable_constructor_browse_with_affiliate_app',
    enable_constructor_search_with_affiliate_app = 'enable_constructor_search_with_affiliate_app',
    enable_show_affiliate_web = 'enable_show_affiliate_web',
    enable_flipgive_app = 'enable_flipgive_app',
    enable_constructor_browse_with_affiliate_web = 'enable_constructor_browse_with_affiliate_web',
    enable_constructor_search_with_affiliate_web = 'enable_constructor_search_with_affiliate_web',
    enable_flipgive_web = 'enable_flipgive_web',
    enable_affiliate_double_dip_banner_app = 'enable_affiliate_double_dip_banner_app',
    enable_appsflyer_event_logging = 'enable_appsflyer_event_logging',
    enable_invite_friends_family_from_header = 'enable_invite_friends_family_from_header',
    enable_mobile_inbox_app = 'enable_mobile_inbox_app',
    enable_mobile_inbox_error_app = 'enable_mobile_inbox_error_app',
    enable_add_favorites_screen_app = 'enable_add_favorites_screen_app',
    enable_affiliate_confirmation_app = 'enable_affiliate_confirmation_app',
    enable_bank_copy_app = 'enable_bank_copy_app',
    enable_affiliate_confirmation_web = 'enable_affiliate_confirmation_web',
    enable_card_linking_checkout_app = 'enable_card_linking_checkout_app',
    enable_card_linking_checkout_web = 'enable_card_linking_checkout_web',
    enable_bex_banner_web = 'enable_bex_banner_web',
    enable_invite_friends_and_family_drawing_app = 'enable_invite_friends_and_family_drawing_app',
    enable_invite_friends_and_family_drawing_web = 'enable_invite_friends_and_family_drawing_web',
    enable_affiliate_double_dip_banner_web = 'enable_affiliate_double_dip_banner_web',
    enable_appsflyer_p2p_app = 'enable_appsflyer_p2p_app',
    enable_appsflyer_p2p_web = 'enable_appsflyer_p2p_web',
    enable_appsflyer_invite_tools_web = 'enable_appsflyer_invite_tools_web',
    enable_attractions_app = 'enable_attractions_app',
    enable_attractions_web = 'enable_attractions_web',
    enable_web_onboarding_modal = 'enable_web_onboarding_modal',
    enable_buy_again_wallet_detail_button_app = 'enable_buy_again_wallet_detail_button_app',
    enable_copy_shop_wallet_app = 'enable_copy_shop_wallet_app',
    enable_copy_shop_full_layout_app = 'enable_copy_shop_full_layout_app',
    enable_p2p_invite_copy_config_app = 'enable_p2p_invite_copy_config_app',
    enable_p2p_invite_copy_config_web = 'enable_p2p_invite_copy_config_web',
    enable_button_browser = 'enable_button_browser',
    enable_posthog_web = 'enable_posthog_web',
    enable_posthog_app = 'enable_posthog_app',
    enable_guided_onboarding_app = 'enable_guided_onboarding_app',
    enable_skip_guided_onboarding_app = 'enable_skip_guided_onboarding_app',
    enable_guided_onboarding_web = 'enable_guided_onboarding_web',
    disable_flipgive_order_history_banner_app = 'disable_flipgive_order_history_banner_app',
    disable_flipgive_order_history_banner_web = 'disable_flipgive_order_history_banner_web',
    disable_flipgive_wallet_banner_app = 'disable_flipgive_wallet_banner_app',
    disable_flipgive_wallet_banner_web = 'disable_flipgive_wallet_banner_web',
    enable_coordinator_invite_hub_web = 'enable_coordinator_invite_hub_web',
    enable_org_earnings_card_web = 'enable_org_earnings_card_web',
    enable_org_earnings_card_app = 'enable_org_earnings_card_app',
    enable_app_home_screen = 'enable_app_home_screen',
    enable_pending_earnings_web = 'enable_pending_earnings_web',
    enable_pending_earnings_app = 'enable_pending_earnings_app',
}

export const WIP_FEATURE_FLAGS: FeatureFlagTypes[] = [];

export interface FeatureFlag {
    [key: string]: boolean;
}

export interface ExperimentalFeatureFlagsState {
    flags: FeatureFlag;
    loading: boolean;
    lastFetch: number;
    loadedCount: number;
    dispatchedKeys: { [key: string]: boolean };
    userIsLoggedIn: boolean;
    userLoginStatusUpdated: boolean;
}

let inFlightFeatureFlags: Promise<FeatureFlag | undefined> | null = null;
// Bumped on logout so a fetch started for the previous user never writes.
let flagsEpoch = 0;

const initialExperimentalFeatureFlagsState: ExperimentalFeatureFlagsState = {
    flags: {},
    loading: false,
    lastFetch: 0,
    loadedCount: 0,
    dispatchedKeys: {},
    userIsLoggedIn: false,
    userLoginStatusUpdated: false,
};

const mapFeatureFlags = (result: Awaited<ReturnType<typeof getCrmFeatureFlags>>): FeatureFlag =>
    (result?.data?.featureFlags || []).reduce(
        (acc, { key, enabled }) => ({ ...acc, [key]: enabled }),
        {}
    );

const fetchAndStoreFeatureFlags = (
    dispatch: Dispatch,
    loadedCount: number
): Promise<FeatureFlag | undefined> => {
    if (inFlightFeatureFlags) {
        return inFlightFeatureFlags;
    }

    const epoch = flagsEpoch;
    const write = (action: AnyAction) => epoch === flagsEpoch && dispatch(action);
    dispatch(update({ loading: true }));

    inFlightFeatureFlags = (async () => {
        try {
            const result = await getCrmFeatureFlags();
            if (result.response?.ok) {
                const flags = mapFeatureFlags(result);
                write(
                    update({
                        flags,
                        loading: false,
                        lastFetch: Date.now(),
                        loadedCount: loadedCount + 1,
                    })
                );
                return flags;
            } else {
                // Handle failure: e.g., setting loading to false, logging, etc.
                write(update({ loading: false }));
            }
        } catch (error) {
            console.error(error);
            write(update({ loading: false }));
        } finally {
            if (epoch === flagsEpoch) {
                inFlightFeatureFlags = null;
            }
        }
    })();

    return inFlightFeatureFlags;
};

export const fetchFeatureFlags = async (
    dispatch: any,
    experimentalFeatureFlags: ExperimentalFeatureFlagsState
) => {
    if (experimentalFeatureFlags?.loading) {
        return inFlightFeatureFlags || experimentalFeatureFlags.flags;
    }

    // Avoid fetching too often
    const lastFetch = experimentalFeatureFlags?.lastFetch || 0;
    if (Date.now() - lastFetch < 1000 * 60 * 10) {
        return experimentalFeatureFlags.flags;
    }

    return fetchAndStoreFeatureFlags(dispatch, experimentalFeatureFlags.loadedCount);
};

export const fetchFeatureFlagsV2 =
    () => async (dispatch: Dispatch, getState: () => FeaturesState) => {
        const state = getState();
        const isAuthenticated = state.store.account.userAuthState === UserAuthState.AUTHENTICATED;

        const experimentalFeatureFlags = state.store.experimentalFeatureFlags;

        if (experimentalFeatureFlags?.loading) {
            return inFlightFeatureFlags || experimentalFeatureFlags.flags;
        }

        if (!isAuthenticated) {
            return experimentalFeatureFlags.flags;
        }

        // Avoid fetching too often
        const lastFetch = experimentalFeatureFlags?.lastFetch || 0;
        if (Date.now() - lastFetch < 1000 * 60 * 10) {
            return experimentalFeatureFlags.flags;
        }

        return fetchAndStoreFeatureFlags(dispatch, experimentalFeatureFlags.loadedCount);
    };

const { update, reducer, configureAction } = createSlice(
    initialExperimentalFeatureFlagsState,
    'EXPERIMENTAL_FEATURE_FLAGS'
);

export const featureFlagsUpdateKeyValue = configureAction<{ key: string; value: boolean }>(
    'updateKeyValue',
    (payload) => (state) => {
        const newFlags = { ...state.flags };
        newFlags[payload.key] = payload.value;
        return { ...state, flags: newFlags };
    }
);

// On logout: lastFetch 0 makes the next login fetch its own flags; the last values stay
// readable until then. A fetch in flight is dropped so the next one starts fresh.
export const resetExperimentalFeatureFlags = () => {
    flagsEpoch += 1;
    inFlightFeatureFlags = null;
    return update({ lastFetch: 0, loading: false });
};

export const experimentalFeatureFlagsReducer = reducer;

export const selectIsEnabled = (state: FeaturesState, key: string) =>
    state.store?.experimentalFeatureFlags.flags[key] ?? false;

export const selectExperimentalFlags = (state: FeaturesState) =>
    state.store?.experimentalFeatureFlags?.flags || {};

export const selectIsIterableEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_iterable);

export const selectIsAppsFlyerP2PAppEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_appsflyer_p2p_app);

export const selectIsAppsFlyerP2PWebEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_appsflyer_p2p_web);

export const selectIsAppsFlyerInviteToolsWebEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_appsflyer_invite_tools_web);

export const selectIsConstructorBrowseEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_constructor_browse_with_affiliate_app
        ];
    }
};

export const selectIsFlipGiveEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[FeatureFlagTypes.enable_flipgive_app];
    }
};

export const selectExperimetnalFlagsLoading = (state: FeaturesState) =>
    state.store?.experimentalFeatureFlags?.loading;

export const selectIsConstructorBrowseEnabledWeb = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_constructor_browse_with_affiliate_web
        ];
    }
};

export const selectIsConstructorSearchEnabledWeb = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_constructor_search_with_affiliate_web
        ];
    }
};

export const selectAffiliateEnabledWeb = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_show_affiliate_web
        ];
    }
};

export const selectIsAffiliateDoubleDipBannerWebEnabled = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_affiliate_double_dip_banner_web
        ];
    }
};

export const selectIsFlipgiveEnabledWeb = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[FeatureFlagTypes.enable_flipgive_web];
    }
};

export const selectIsAffiliateDoubleDipBannerEnabled = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_affiliate_double_dip_banner_app
        ];
    }
};

export const selectIsBulkEgiftingEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[FeatureFlagTypes.enable_bulk_egifting];
    }
};

export const selectIsMobileInboxAppEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_mobile_inbox_app
        ];
    }
};

export const selectIsMobileInboxErrorAppEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_mobile_inbox_error_app
        ];
    }
};

export const selectIsAddFavoritesScreenEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_add_favorites_screen_app
        ];
    }
};

export const selectIsAffiliateConfirmationAppEnabled = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_affiliate_confirmation_app
        ];
    }
};

export const selectIsBankCopyAppEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[FeatureFlagTypes.enable_bank_copy_app];
    }
};

export const selectIsAffiliateConfirmationWebEnabled = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_affiliate_confirmation_web
        ];
    }
};

export const selectIsP2PEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_invite_friends_family
        ];
    }
};

export const selectIsP2PContestEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_invite_friends_and_family_drawing_app
        ];
    }
};

export const selectIsCardLinkingThroughCheckoutAppEnabled = (
    state: FeaturesState
): boolean | undefined => {
    if (IS_FLIPGIVE) {
        return false;
    }
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_card_linking_checkout_app
        ];
    }
};

export const selectIsCardLinkingThroughCheckoutWebEnabled = (
    state: FeaturesState
): boolean | undefined => {
    if (IS_FLIPGIVE) {
        return false;
    }

    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_card_linking_checkout_web
        ];
    }
};

export const selectIsInviteFriendsFamilyDrawingWebEnabled = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_invite_friends_and_family_drawing_web
        ];
    }
};

export const selectHasFetchedFeatureFlags = (state: FeaturesState) => {
    return state.store.experimentalFeatureFlags.lastFetch > 0;
};

export const selectIsAttractionsWebEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_attractions_web
        ];
    }
};

export const selectIsWebOnboardingModalEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_web_onboarding_modal
        ];
    }
};

export const selectIsGuidedOnboardingWebEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.enable_guided_onboarding_web
        ];
    }
};

export const selectIsP2PInviteCopyConfigAppEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_p2p_invite_copy_config_app);

export const selectIsP2PInviteCopyConfigWebEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_p2p_invite_copy_config_web);

export const selectIsPosthogWebEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_posthog_web);

export const selectIsCoordinatorInviteHubWebEnabled = (state: FeaturesState) =>
    selectIsEnabled(state, FeatureFlagTypes.enable_coordinator_invite_hub_web);

export const selectIsButtonBrowserEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[FeatureFlagTypes.enable_button_browser];
    }
};

export const selectIsPosthogAppEnabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[FeatureFlagTypes.enable_posthog_app];
    }
};

export const selectIsFlipgiveOrderHistoryBannerDisabled = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.disable_flipgive_order_history_banner_app
        ];
    }
};

export const selectIsFlipgiveOrderHistoryBannerDisabledWeb = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.disable_flipgive_order_history_banner_web
        ];
    }
};

export const selectIsFlipgiveWalletBannerDisabled = (state: FeaturesState): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.disable_flipgive_wallet_banner_app
        ];
    }
};

export const selectIsFlipgiveWalletBannerDisabledWeb = (
    state: FeaturesState
): boolean | undefined => {
    if (state.store?.experimentalFeatureFlags?.flags) {
        return state.store?.experimentalFeatureFlags?.flags[
            FeatureFlagTypes.disable_flipgive_wallet_banner_web
        ];
    }
};

/*
Doc:
Make sure to memoize this with useCallback to avoid recreating the selector.
 */
export const checkFeatureFlag = (flag: FeatureFlagTypes) =>
    createSelector(
        (state: FeaturesState) => state.store.experimentalFeatureFlags.flags,
        (flags: FeatureFlag) => {
            if (flags) {
                return flags[flag];
            }
            return undefined;
        }
    );
